import React, { useState, useCallback } from 'react';
import axios from 'axios';
import AppLayout from '../components/AppLayout';
import toast from 'react-hot-toast';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Upload, 
  Play, 
  Download, 
  FileSpreadsheet, 
  Trash2,
  Bot,
  CheckCircle,
  Loader2
} from 'lucide-react';
import * as XLSX from 'xlsx';

interface ScanResult {
  productId: string;
  productName: string;
  result: string;
  analyzedAt: Date;
}

const AdminCustomScan: React.FC = () => {
  // State
  const [productIds, setProductIds] = useState<string[]>([]);
  const [customPrompt, setCustomPrompt] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [scanResults, setScanResults] = useState<ScanResult[]>([]);
  const [currentScanId, setCurrentScanId] = useState<string | null>(null);
  const [progress, setProgress] = useState({ processed: 0, total: 0 });

  // Örnek promptlar
  const samplePrompts = [
    'Bu ürünün açıklaması yeterli mi? Eksik bilgiler var mı?',
    'Bu ürün için SEO optimizasyonu önerileri nelerdir?',
    'Ürün açıklaması ve adı tutarlı mı?',
    'Bu ürünün hedef kitlesi kim olmalı?',
    'Ürün fiyatlandırması için değerlendirme yap.',
  ];

  // Excel dosyası yükleme
  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];

        // İlk sütundaki ID'leri al (başlık satırını atla)
        // Hem MongoDB ObjectId hem de Ticimax ID (sayısal) destekleniyor
        const ids = jsonData
          .slice(1)
          .map(row => row[0]?.toString().trim())
          .filter(id => id && id.length > 0);

        if (ids.length === 0) {
          toast.error('Excel dosyasında ürün ID bulunamadı!');
          return;
        }

        // ID tipini belirle ve kullanıcıyı bilgilendir
        const isObjectId = (id: string) => /^[a-f\d]{24}$/i.test(id);
        const objectIdCount = ids.filter(isObjectId).length;
        const numericIdCount = ids.filter(id => /^\d+$/.test(id)).length;
        
        setProductIds(ids);
        
        if (objectIdCount > 0 && numericIdCount > 0) {
          toast.success(`${ids.length} ID yüklendi (${objectIdCount} MongoDB ID, ${numericIdCount} Ticimax ID)`);
        } else if (numericIdCount > 0) {
          toast.success(`${ids.length} Ticimax ID yüklendi!`);
        } else {
          toast.success(`${ids.length} ürün ID yüklendi!`);
        }
      } catch (error) {
        toast.error('Excel dosyası okunamadı!');
        console.error('Excel parse error:', error);
      }
    };
    reader.readAsArrayBuffer(file);
  }, []);

  // Taramayı başlat
  const handleStartScan = async () => {
    if (productIds.length === 0) {
      toast.error('Lütfen önce ürün ID\'lerini yükleyin!');
      return;
    }

    if (!customPrompt.trim()) {
      toast.error('Lütfen bir prompt giriniz!');
      return;
    }

    setIsScanning(true);
    setProgress({ processed: 0, total: productIds.length });
    setScanResults([]);

    try {
      const response = await axios.post('/openai/custom-scan', {
        productIds,
        customPrompt: customPrompt.trim(),
      });

      if (response.data.status === 'success') {
        setCurrentScanId(response.data.scanId);
        setProgress({ processed: response.data.processed, total: productIds.length });
        
        // Sonuçları getir
        const resultsResponse = await axios.get(`/openai/custom-scan/${response.data.scanId}`);
        setScanResults(resultsResponse.data.results || []);
        
        toast.success(`Tarama tamamlandı! ${response.data.processed} ürün işlendi.`);
      }
    } catch (error: any) {
      toast.error('Tarama başarısız: ' + (error.response?.data?.message || error.message));
    } finally {
      setIsScanning(false);
    }
  };

  // Sonuçları Excel olarak indir
  const handleExportResults = async () => {
    if (!currentScanId || scanResults.length === 0) {
      toast.error('İndirilecek sonuç yok!');
      return;
    }

    try {
      const response = await axios.get(`/openai/custom-scan/${currentScanId}/export`);
      const { headers, rows } = response.data.data;

      // Excel oluştur
      const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'AI Analiz Sonuçları');
      
      // İndir
      XLSX.writeFile(wb, `ai-scan-results-${currentScanId}.xlsx`);
      toast.success('Excel dosyası indirildi!');
    } catch (error: any) {
      toast.error('Excel export başarısız: ' + (error.response?.data?.message || error.message));
    }
  };

  // Listeyi temizle
  const handleClear = () => {
    setProductIds([]);
    setCustomPrompt('');
    setScanResults([]);
    setCurrentScanId(null);
    setProgress({ processed: 0, total: 0 });
  };

  return (
    <AppLayout title="Özel AI Tarama" subtitle="Excel ile ürün ID yükle, özel prompt ile AI sorgula">
      
      <div className="container mx-auto px-4 py-8 space-y-6">
        {/* Bilgi Kartı */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Bot className="h-5 w-5 text-blue-600 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-medium text-blue-900">Özel AI Tarama</h3>
              <p className="text-sm text-blue-700 mt-1">
                Excel ile ürün ID'lerini yükleyin, özel bir prompt girin ve AI ile ürünlerinizi analiz edin.
                Sonuçlar hem ürün kaydına yazılır hem de Excel olarak indirilebilir.
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Sol Panel - Giriş */}
          <div className="space-y-6">
            {/* Excel Yükleme */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileSpreadsheet className="w-5 h-5" />
                  Ürün ID'lerini Yükle
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <input
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="excel-upload"
                  />
                  <label
                    htmlFor="excel-upload"
                    className="cursor-pointer flex flex-col items-center gap-2"
                  >
                    <Upload className="w-8 h-8 text-gray-400" />
                    <span className="text-sm text-gray-600">
                      Excel dosyası seçin veya sürükleyin
                    </span>
                    <span className="text-xs text-gray-400">
                      İlk sütunda ürün ID'leri olmalı (.xlsx, .xls, .csv)
                    </span>
                  </label>
                </div>

                {productIds.length > 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span className="text-sm font-medium text-green-900">
                          {productIds.length} ürün ID yüklendi
                        </span>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setProductIds([])}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="mt-2 max-h-32 overflow-y-auto">
                      <div className="flex flex-wrap gap-1">
                        {productIds.slice(0, 20).map((id, index) => (
                          <Badge key={index} variant="secondary" className="text-xs">
                            {id.substring(0, 12)}...
                          </Badge>
                        ))}
                        {productIds.length > 20 && (
                          <Badge variant="outline" className="text-xs">
                            +{productIds.length - 20} daha
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Prompt Girişi */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Bot className="w-5 h-5" />
                  AI Prompt
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Örnek Promptlar</label>
                  <div className="flex flex-wrap gap-2">
                    {samplePrompts.map((prompt, index) => (
                      <Button
                        key={index}
                        size="sm"
                        variant="outline"
                        onClick={() => setCustomPrompt(prompt)}
                        className="text-xs"
                      >
                        {prompt.substring(0, 30)}...
                      </Button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Özel Prompt *</label>
                  <textarea
                    value={customPrompt}
                    onChange={(e) => setCustomPrompt(e.target.value)}
                    placeholder="AI'ya sormak istediğiniz soruyu yazın..."
                    className="w-full h-32 px-3 py-2 border rounded-md resize-none"
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    className="flex-1"
                    onClick={handleStartScan}
                    disabled={isScanning || productIds.length === 0 || !customPrompt.trim()}
                  >
                    {isScanning ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Taranıyor... ({progress.processed}/{progress.total})
                      </>
                    ) : (
                      <>
                        <Play className="w-4 h-4 mr-2" />
                        Taramayı Başlat
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleClear}
                    disabled={isScanning}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Temizle
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sağ Panel - Sonuçlar */}
          <Card className="h-fit">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Tarama Sonuçları</CardTitle>
              {scanResults.length > 0 && (
                <Button size="sm" variant="outline" onClick={handleExportResults}>
                  <Download className="w-4 h-4 mr-2" />
                  Excel İndir
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {scanResults.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <Bot className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Henüz tarama sonucu yok</p>
                  <p className="text-sm mt-1">
                    Ürün ID'lerini yükleyin ve prompt girerek taramayı başlatın
                  </p>
                </div>
              ) : (
                <div className="space-y-4 max-h-[600px] overflow-y-auto">
                  {scanResults.map((result, index) => (
                    <div
                      key={index}
                      className="border rounded-lg p-4 bg-gray-50"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="font-medium text-sm">{result.productName}</h4>
                          <p className="text-xs text-gray-500 font-mono">
                            {result.productId}
                          </p>
                        </div>
                        <Badge variant="secondary" className="text-xs">
                          {new Date(result.analyzedAt).toLocaleString('tr-TR')}
                        </Badge>
                      </div>
                      <div className="bg-white rounded p-3 text-sm">
                        {result.result}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AppLayout>
  );
};

export default AdminCustomScan;
